import type { HTMLAttributes } from 'react'
import {
	Header as AriaHeader,
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	type MenuItemProps as AriaMenuItemProps,
	Popover as AriaPopover,
	type PopoverProps as AriaPopoverProps,
	Separator as AriaSeparator,
	type SeparatorProps as AriaSeparatorProps,
	Button,
	MenuTrigger,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Props for the {@link MenuContent} component.
 *
 * @property placement - Popover placement relative to the trigger (default 'bottom start')
 * @property className - Optional additional Tailwind classes, merged over the chrome
 * @property children - MenuItem / MenuHeader / MenuSeparator entries
 */
export interface MenuContentProps extends Omit<AriaPopoverProps, 'className' | 'children'> {
	className?: string
	children: React.ReactNode
}

/**
 * Accessible dropdown menu built on React Aria Components.
 *
 * @remarks
 * - **Composition:** `MenuTrigger` wraps a press-aware trigger (the design-system
 *   `Button`, or `MenuTriggerButton` for bespoke looks) and a `MenuContent`.
 * - **Selection:** each `MenuItem` takes `onAction`; the menu closes on select.
 * - **Keyboard:** arrow-key navigation; `Escape` closes and restores focus to the trigger.
 * - **Styling:** all colors, spacing, and sizing use design tokens from `@atlas/tokens`.
 *
 * @example
 * ```tsx
 * <MenuTrigger>
 *   <Button variant="outline" size="sm" aria-label="Row actions">…</Button>
 *   <MenuContent placement="bottom end">
 *     <MenuItem onAction={edit}>Edit</MenuItem>
 *     <MenuItem onAction={remove} isDisabled={busy}>Delete</MenuItem>
 *   </MenuContent>
 * </MenuTrigger>
 * ```
 */
export function MenuContent({ className, children, ...props }: MenuContentProps) {
	return (
		<AriaPopover
			{...props}
			className={cn('min-w-32 rounded-ds-md border border-ds-border bg-ds-background p-1 shadow-ds-lg', className)}
		>
			<AriaMenu className="outline-none">{children}</AriaMenu>
		</AriaPopover>
	)
}

/**
 * Props for the {@link MenuItem} component.
 *
 * @property onAction - Called when the item is selected
 * @property isDisabled - Disables the item (kept queryable, marked `aria-disabled`)
 * @property id - Stable key for dynamic collections
 * @property className - Optional additional Tailwind classes
 */
export interface MenuItemProps extends Omit<AriaMenuItemProps, 'className'> {
	className?: string
}

/**
 * One entry in a menu. Renders `role="menuitem"`; focus and disabled states are
 * styled via `data-[focused]` / `data-[disabled]`.
 */
export function MenuItem({ className, ...props }: MenuItemProps) {
	return (
		<AriaMenuItem
			{...props}
			className={cn(
				'flex cursor-default select-none items-center gap-2 rounded-ds-sm px-2 py-1.5 text-ds-sm text-ds-text outline-none transition-colors data-[focused]:bg-ds-surface data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
				className,
			)}
		/>
	)
}

/**
 * Non-interactive heading inside a menu (e.g. the signed-in user's email).
 */
export function MenuHeader({ className, ...props }: HTMLAttributes<HTMLElement>) {
	return <AriaHeader {...props} className={cn('px-2 py-1.5 text-ds-sm font-semibold text-ds-text', className)} />
}

/**
 * Props for the {@link MenuSeparator} component.
 */
export interface MenuSeparatorProps extends Omit<AriaSeparatorProps, 'className'> {
	className?: string
}

/**
 * Horizontal rule between menu entries.
 */
export function MenuSeparator({ className, ...props }: MenuSeparatorProps) {
	return <AriaSeparator {...props} className={cn('-mx-1 my-1 h-px bg-ds-border', className)} />
}

// Re-exports: the trigger context, and an unstyled press-aware button for bespoke trigger looks
export { Button as MenuTriggerButton, MenuTrigger }
